import confetti from 'canvas-confetti';
import { useState, useEffect, useMemo } from 'react';
import './App.css';
import AddServerModal from './AddServerModal.js';
import ReportMenu from './ReportMenu.js';
import { recordClick } from './session.js';
// Bundled snapshot used for first paint and as an offline/failure fallback.
// Live data comes from /api/metadata (CDN-cached; see api/metadata.js).
import fallbackMetadata from './server-metadata.json';

const GITHUB_REPO = 'adamnelsonarcher/uhcs-disc';

/** Extract the invite code from a Discord invite URL. */
function inviteCodeFrom(link) {
  if (!link) return null;
  return link.replace(/\/$/, '').split('/').pop() || null;
}

/** Format a star count like the common badge (e.g. 1.8k). */
function formatStarCount(n) {
  if (n == null) return '–';
  if (n < 1000) return String(n);
  const k = n / 1000;
  const rounded = k >= 10 ? Math.round(k) : Math.round(k * 10) / 10;
  return `${rounded}k`;
}

/**
 * Attach Discord metadata (name / icon / member count) to each professor.
 * Catalog rows come from Neon via /api/servers (or servers.json offline fallback).
 * Invite links are only shown when Discord metadata confirms they still resolve
 * (approval in Neon does not mean the invite is still valid).
 */
function enrichCourses(rawCourses, metadata) {
  const servers = metadata?.servers || {};

  return rawCourses.map((course) => ({
    ...course,
    professors: (course.professors || [])
      .filter((prof) => {
        if (prof.isDM) return true;
        if (!prof.link) return false;
        const meta = servers[inviteCodeFrom(prof.link)];
        return Boolean(meta && meta.memberCount);
      })
      .map((prof) => {
        const code = inviteCodeFrom(prof.link);
        const meta = code ? servers[code] : null;
        return {
          ...prof,
          serverName: meta?.name || 'Discord Server',
          serverIcon: meta?.icon || null,
          memberCount: meta?.memberCount,
          serverId: prof.serverId || null,
          clickCount: prof.clickCount || 0,
        };
      }),
  }));
}

function getCourseStats(courses) {
  const totalCourses = courses.length;
  const coursesWithDiscord = courses.filter((c) => c.professors.length > 0).length;
  return { totalCourses, coursesWithDiscord };
}

function getServerStats(courses, metadata) {
  let largest = { name: '', count: 0 };
  let smallest = { name: '', count: Number.MAX_VALUE };
  let total = 0;
  let count = 0;
  let totalClicks = 0;
  const levelCounts = { 1000: 0, 2000: 0, 3000: 0, 4000: 0, 6000: 0 };
  const professorCounts = {};

  courses.forEach((course) => {
    const level = Math.floor(course.courseId / 1000) * 1000;
    if (!(level in levelCounts)) return;

    course.professors.forEach((prof) => {
      if (!prof.link) return;
      levelCounts[level] += 1;
      professorCounts[prof.name] = (professorCounts[prof.name] || 0) + 1;
      totalClicks += Number(prof.clickCount) || 0;
    });
  });

  let topProfessor = { name: '', count: 0 };
  Object.entries(professorCounts).forEach(([name, n]) => {
    if (n > topProfessor.count) topProfessor = { name, count: n };
  });

  Object.values(metadata?.servers || {}).forEach((server) => {
    if (typeof server.memberCount !== 'number') return;
    if (server.memberCount > largest.count) {
      largest = { name: server.name, count: server.memberCount };
    }
    if (server.memberCount < smallest.count) {
      smallest = { name: server.name, count: server.memberCount };
    }
    total += server.memberCount;
    count += 1;
  });

  return {
    largest,
    smallest,
    total,
    average: count ? total / count : 0,
    levelCounts,
    topProfessor,
    totalClicks,
  };
}

function App() {
  const [rawCourses, setRawCourses] = useState([]);
  const [metadata, setMetadata] = useState(fallbackMetadata);
  const [expandedCourses, setExpandedCourses] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showStats, setShowStats] = useState(false);
  const [hideEmptyCourses, setHideEmptyCourses] = useState(false);
  const [showAddServer, setShowAddServer] = useState(false);
  const [stars, setStars] = useState(null);

  const courses = useMemo(
    () => enrichCourses(rawCourses, metadata),
    [rawCourses, metadata]
  );

  const courseStats = useMemo(() => getCourseStats(courses), [courses]);
  const serverStats = useMemo(
    () => getServerStats(courses, metadata),
    [courses, metadata]
  );

  // Course catalog from Neon. Falls back to the static JSON seed under
  // `npm start` (no API) or if the DB endpoint is unreachable.
  useEffect(() => {
    fetch('/api/servers')
      .then((response) =>
        response.ok ? response.json() : Promise.reject(response.status)
      )
      .then((data) => {
        if (Array.isArray(data?.courses) && data.courses.length > 0) {
          setRawCourses(data.courses);
          return null;
        }
        return Promise.reject('empty catalog');
      })
      .catch(() =>
        fetch('/servers.json')
          .then((response) => response.json())
          .then(setRawCourses)
          .catch((error) => console.error('Error loading courses:', error))
      );
  }, []);

  // Live Discord metadata; keep the bundled fallback if the request fails
  // (expected under `npm start`, since CRA does not serve /api/*).
  useEffect(() => {
    fetch('/api/metadata')
      .then((response) =>
        response.ok ? response.json() : Promise.reject(response.status)
      )
      .then((data) => {
        if (data?.servers) setMetadata(data);
      })
      .catch((error) => console.warn('Using bundled metadata fallback:', error));
  }, []);

  // GitHub star count for the pill badge next to the credits.
  useEffect(() => {
    fetch(`https://api.github.com/repos/${GITHUB_REPO}`)
      .then((response) =>
        response.ok ? response.json() : Promise.reject(response.status)
      )
      .then((data) => {
        if (typeof data?.stargazers_count === 'number') {
          setStars(data.stargazers_count);
        }
      })
      .catch(() => {});
  }, []);

  const filteredCourses = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    const normalizedSearch = searchLower.replace(/^cosc/, '').trim();

    return courses.filter((course) => {
      if (hideEmptyCourses && course.professors.length === 0) return false;

      // Hide empty graduate courses by default.
      if (course.courseId >= 6000 && course.professors.length === 0) return false;

      return (
        course.courseId.toString().includes(normalizedSearch) ||
        `cosc${course.courseId}`.toLowerCase().includes(searchLower) ||
        course.courseName.toLowerCase().includes(searchLower) ||
        course.professors.some((prof) =>
          prof.name.toLowerCase().includes(searchLower)
        )
      );
    });
  }, [courses, searchTerm, hideEmptyCourses]);

  const toggleCourse = (courseId) => {
    setExpandedCourses((prev) => ({
      ...prev,
      [courseId]: !prev[courseId],
    }));
  };

  const handleBackgroundClick = (e) => {
    const isOnCard =
      e.target.closest('.course-card') || e.target.closest('.professor-card');
    const isOnUi = e.target.closest(
      'button, a, input, select, label, textarea, .search-container, .hide-empty-toggle, .add-server-cta, .credits, .stats-popup, .header-info, h1'
    );
    const isMobile = window.innerWidth <= 768;

    if (isOnCard || isOnUi || isMobile) return;

    try {
      confetti({
        particleCount: 25,
        spread: 50,
        startVelocity: 5,
        gravity: 1,
        ticks: 500,
        origin: {
          x: e.clientX / window.innerWidth,
          y: e.clientY / window.innerHeight,
        },
        colors: ['#FF0000', '#FF0000', '#FFFFFF'],
        zIndex: 0,
        scalar: 0.7,
        angle: 90,
        drift: 0,
      });
    } catch (error) {
      console.error('Error launching confetti:', error);
    }
  };

  const {
    largest,
    smallest,
    total,
    average,
    levelCounts,
    topProfessor,
    totalClicks,
  } = serverStats;

  return (
    <div className="App" onClick={handleBackgroundClick}>
      <header className="App-header">
        <div className="header-content">
          <h1>UH Computer Science Discord Links</h1>
          <button
            type="button"
            className="stats-button"
            onClick={(e) => {
              e.stopPropagation();
              setShowStats(true);
            }}
          >
            <span className="stats-icon" aria-hidden="true">
              📊
            </span>
            <span className="stats-text">Server Stats</span>
          </button>

          <div className="header-info">
            <p className="stats">
              {courseStats.coursesWithDiscord}/{courseStats.totalCourses} courses
              have Discord servers
            </p>
          </div>

          <div className="add-server-cta">
            <button
              type="button"
              className="add-server-link"
              onClick={(e) => {
                e.stopPropagation();
                setShowAddServer(true);
              }}
            >
              + Submit new server
            </button>
          </div>

          <div className="search-container">
            <input
              type="text"
              placeholder="Search courses or professors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <p className="credits">
            <span>
              Site created by{' '}
              <a
                href="https://nelsonarcher.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Adam
              </a>
              .
            </span>
            <a
              className="github-star-pill"
              href={`https://github.com/${GITHUB_REPO}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Star ${GITHUB_REPO} on GitHub`}
            >
              <span className="github-star-label">uhcs-disc</span>
              <span className="github-star-icon" aria-hidden="true">
                ★
              </span>
              <span>{formatStarCount(stars)}</span>
            </a>
          </p>
        </div>

        {showAddServer && (
          <AddServerModal
            courses={rawCourses}
            onClose={() => setShowAddServer(false)}
          />
        )}

        {showStats && (
          <div className="stats-popup" onClick={(e) => e.stopPropagation()}>
            <div className="stats-popup-content">
              <div className="stats-header">
                <h3>Fun Server Stats</h3>
                <button
                  type="button"
                  className="close-button"
                  onClick={() => setShowStats(false)}
                  aria-label="Close stats"
                >
                  ✕
                </button>
              </div>

              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-title">Largest Server</div>
                  <div className="stat-count">{largest.count} members</div>
                  <div className="stat-server">{largest.name}</div>
                </div>

                <div className="stat-card">
                  <div className="stat-title">Smallest Server</div>
                  <div className="stat-count">{smallest.count} members</div>
                  <div className="stat-server">{smallest.name}</div>
                </div>

                <div className="stat-card">
                  <div className="stat-title">Average Size</div>
                  <div className="stat-count">{Math.round(average)} members</div>
                  <div className="stat-server">per server</div>
                </div>

                <div className="stat-card">
                  <div className="stat-title">Total Members</div>
                  <div className="stat-count">
                    {total.toLocaleString()} members
                  </div>
                  <div className="stat-server">across all servers</div>
                </div>

                <div className="stat-card wide">
                  <div className="stat-title">Servers by Course Level</div>
                  <div className="level-grid">
                    {Object.entries(levelCounts).map(([level, n]) => (
                      <div key={level} className="level-stat">
                        <div className="level-name">{level}s</div>
                        <div className="level-count">{n}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-title">Most servers by Prof</div>
                  <div className="stat-count">Dr. {topProfessor.name}</div>
                  <div className="stat-server">
                    {topProfessor.count} servers
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-title">Total Link Clicks</div>
                  <div className="stat-count">
                    {totalClicks.toLocaleString()}
                  </div>
                  <div className="stat-server">servers joined</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="discord-links">
          <div className="hide-empty-toggle">
            <label>
              <input
                type="checkbox"
                checked={hideEmptyCourses}
                onChange={(e) => setHideEmptyCourses(e.target.checked)}
              />
              <span>Hide courses with no links</span>
            </label>
          </div>

          {filteredCourses.map((course, courseIndex) => {
            const isGraduate = course.courseId >= 6000;
            const isFirstGraduate =
              isGraduate &&
              (courseIndex === 0 ||
                filteredCourses[courseIndex - 1].courseId < 6000);
            const isExpanded = Boolean(expandedCourses[course.courseId]);
            const hasLinks = course.professors.length > 0;

            return (
              <div key={course.courseId}>
                {isFirstGraduate && (
                  <div className="course-divider">
                    <span>Grad Courses</span>
                  </div>
                )}
                <div
                  className={`course-card ${hasLinks ? 'has-links' : 'empty-course'}${isExpanded ? ' expanded' : ''}`}
                  onClick={() => hasLinks && toggleCourse(course.courseId)}
                  onKeyDown={(e) => {
                    if (!hasLinks) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleCourse(course.courseId);
                    }
                  }}
                  role={hasLinks ? 'button' : undefined}
                  tabIndex={hasLinks ? 0 : undefined}
                >
                  <div className="course-header">
                    <span className="course-id">
                      COSC{'\n'}
                      {course.courseId}
                    </span>
                    <span className="course-name">{course.courseName}</span>
                    <div className="course-info">
                      {hasLinks ? (
                        <>
                          <span className="server-count">
                            {course.professors.length}
                          </span>
                          <span className="expand-icon">
                            {isExpanded ? '▼' : '▶'}
                          </span>
                        </>
                      ) : (
                        <span className="server-count empty">0</span>
                      )}
                    </div>
                  </div>

                  {hasLinks && isExpanded && (
                    <div className="professor-grid">
                      {course.professors.map((prof) =>
                        prof.isDM ? (
                          <div
                            key={`${prof.name}-dm`}
                            className="professor-card dm-card"
                          >
                            <span className="prof-name">{prof.name}</span>
                          </div>
                        ) : (
                          <div
                            className="professor-card-wrapper"
                            key={prof.link || `${prof.name}-${course.courseId}`}
                          >
                            {prof.serverId && (
                              <ReportMenu serverId={prof.serverId} />
                            )}
                            <a
                              href={prof.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="professor-card"
                              onClick={() => recordClick(prof.serverId)}
                            >
                            <div
                              className={`server-icon${prof.serverIcon ? '' : ' no-image'}`}
                            >
                              {prof.serverIcon && (
                                <img
                                  src={prof.serverIcon}
                                  alt=""
                                  onError={(e) => {
                                    // Keep a gray circle instead of a broken-image icon.
                                    e.currentTarget.style.display = 'none';
                                    e.currentTarget.parentElement?.classList.add(
                                      'no-image'
                                    );
                                  }}
                                />
                              )}
                            </div>
                            <div className="prof-name">{prof.name}</div>
                            <div className="server-name">{prof.serverName}</div>
                            <div className="card-footer">
                              {(prof.memberCount != null || prof.clickCount > 0) && (
                                <div className="card-stats">
                                  {prof.memberCount != null && (
                                    <div className="member-count">
                                      {prof.memberCount.toLocaleString()} People
                                    </div>
                                  )}
                                  {prof.clickCount > 0 && (
                                    <div className="click-count">
                                      {prof.clickCount.toLocaleString()}{' '}
                                      {prof.clickCount === 1 ? 'click' : 'clicks'}
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="join-text">Click to join</div>
                            </div>
                            </a>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </header>
    </div>
  );
}

export default App;
